import FormattedMessage from 'I18n/components/FormattedMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { propertyFieldTypes } from '../../../constants/properties';
import { pluralTypeToSingle } from '../../../constants/types';
import { AppContext } from '../../../contexts/app/context';
import { PropertiesContext } from '../../../contexts/properties/context';
import useProsemirrorEditor from '../../../hooks/useProsemirrorEditor';
import { prettifyAppName } from '../../../utils/general';
import FormInput from './FormInput';
import { jsx as _jsx } from "react/jsx-runtime";
function FormInputs({
  property,
  onFormValueChanged,
  currentValue,
  editMode = false,
  disabled = false,
  objectType,
  portalId,
  snippetsApiWrapper,
  openUrl,
  correlatedField,
  correlatedFieldValue,
  prosemirrorTrackerProperties,
  autoFocus,
  hubspotUserId
}) {
  const {
    appName
  } = useContext(AppContext);
  const {
    mentionOwners,
    propertiesActions
  } = useContext(PropertiesContext);
  const inputRef = useRef(null);
  const [isUserTriggerAction, setIsUserTriggerAction] = useState(false);
  const currentValueToString = useMemo(() => currentValue instanceof Date ? String(Number(currentValue)) : currentValue || '', [currentValue]);
  const currentValueRef = useRef(currentValueToString);
  const onInputClick = () => {
    if (!isUserTriggerAction) {
      setIsUserTriggerAction(true);
    }
  };
  const onChange = useCallback(arg => {
    if (isUserTriggerAction && !property.externalOptions && !property.options.length) {
      propertiesActions.setIsValidating({
        value: true,
        propertyName: property.name
      });
    }
    onFormValueChanged(arg);
  }, [isUserTriggerAction, onFormValueChanged, propertiesActions, property]);
  const onProseMirrorContentChange = useCallback(newEditorState => {
    onChange({
      key: property.name,
      value: newEditorState,
      required: property.required || false
    });
  }, [onChange, property]);
  const {
    blankComponent,
    prosemirrorEditor
  } = useProsemirrorEditor({
    mentionOwners,
    apiWrapper: snippetsApiWrapper,
    portalId,
    openUrl,
    needToInit: property.fieldType === propertyFieldTypes.html,
    initialValue: currentValueToString,
    onChange: onProseMirrorContentChange,
    prosemirrorTrackerProperties
  });

  // sync currentValueRef before user fire an action
  // to avoid unnecessary calls to validation property api
  useEffect(() => {
    if (!isUserTriggerAction && currentValueRef.current !== currentValue) {
      currentValueRef.current = currentValueToString;
    }
  }, [currentValue, isUserTriggerAction, currentValueToString]);
  useEffect(() => {
    propertiesActions.resetPropertyValidationErrors(property.name);
  }, [currentValue, propertiesActions, property]);

  // validate property value as soon as debouncedValue has been changed
  // otherwise we reset responses from validation api if user removes values from input
  useEffect(() => {
    // validate value if we have one from user
    if (isUserTriggerAction && !property.externalOptions && !property.options.length && currentValueRef.current !== currentValueToString && objectType) {
      propertiesActions.validateProperty({
        data: [{
          name: property.name,
          value: currentValueToString
        }],
        crmObjectType: pluralTypeToSingle[objectType]
      });
    }
    currentValueRef.current = currentValueToString;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValueToString, property, isUserTriggerAction, objectType]);
  useEffect(() => {
    if (inputRef.current && autoFocus) {
      inputRef.current.focus();
    }
  }, [autoFocus]);
  return /*#__PURE__*/_jsx("div", {
    onClick: onInputClick,
    children: /*#__PURE__*/_jsx(Tooltip, {
      title: property.externalOptions ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "associationsPanel.tabs.create.tooltip",
        options: {
          appName: prettifyAppName(appName)
        }
      }) : null,
      children: /*#__PURE__*/_jsx(FormInput, {
        property: property,
        onChange: onChange,
        currentValue: currentValue,
        editMode: editMode,
        disabled: disabled,
        inputRef: inputRef,
        objectType: objectType,
        correlatedField: correlatedField,
        correlatedFieldValue: correlatedFieldValue,
        autoFocus: autoFocus,
        hubspotUserId: hubspotUserId,
        prosemirrorEditor: blankComponent || prosemirrorEditor
      })
    })
  });
}
export default /*#__PURE__*/memo(FormInputs);